
export interface ICommonService {
    isColorDark(color: string): boolean,
    getResolvedColor(variable: string): string,
    trimCssVariable(variable: string): string,
    hexToRgb(hex: string): string | null,
    colorNameToRgb(colorName: string): string | null,
}
class CommonService implements ICommonService {

    isColorDark(color: string): boolean {
        if (color.startsWith('var(')) {
            // Extract the CSS variable name
            const variableName = this.trimCssVariable(color);
            // Resolve the CSS variable to its actual value
            color = this.getResolvedColor(variableName);
        }

        if (color.startsWith('--')) {
            color = this.getResolvedColor(color);
        }

        if (color.startsWith('rgba')) {
            color = this.rgbaToRgb(color);
        }

        if (!color.startsWith('rgb') &&
            !color.startsWith('#') &&
            !color.startsWith('var(') &&
            !color.startsWith('--')) {
            color = this.colorNameToRgb(color) || '';
        }

        if (color.startsWith('#')) {
            color = this.hexToRgb(color) || '';
        }

        // Check if the resolved color is in RGB format
        if (color.startsWith('rgb')) {
            const rgb = color.replace(/[^\d,]/g, '').split(',');
            const [r, g, b] = rgb.map(Number);
            // Calculate luminance
            const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
            return luminance < 128; // Threshold for light/dark
        }

        // If the color is not in a recognized format, return false by default
        console.warn('Unsupported color format:', color);
        return false;
    }

    getResolvedColor(variable: string): string {
        return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
    }

    trimCssVariable(variable: string): string {
        return variable.replace(/^var\((.+)\)$/, '$1');
    }

    hexToRgb(hex: string): string | null {
        // Remove the hash (#) if it exists
        hex = hex.replace(/^#/, '');

        // Check if the hex is in 3-digit or 6-digit format
        if (hex.length === 3) {
            // Expand shorthand (e.g., "abc" -> "aabbcc")
            hex = hex.split('').map(char => char + char).join('');
        }

        if (hex.length !== 6) {
            console.warn('Invalid hex color:', hex);
            return null; // Return null for invalid hex colors
        }

        // Parse the red, green, and blue components
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `rgb(${r}, ${g}, ${b})`;
    }

    colorNameToRgb(colorName: string): string | null {
        // Create a temporary canvas element
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (!context) {
            console.warn('Canvas context is not available.');
            return null;
        }

        // Set the fill style to the color name
        context.fillStyle = colorName;

        // Check if the browser recognizes the color name
        if (context.fillStyle === '') {
            console.warn('Invalid color name:', colorName);
            return null;
        }

        // Get the computed color in RGB format
        return context.fillStyle;
    }

    rgbaToRgb(rgba: string, background: [number, number, number] = [255, 255, 255]): string | null {
        const match = rgba.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/);
        if (!match) return null;
        const [, r, g, b, a] = match;
        const alpha = parseFloat(a);
        const blend = (c: number, bg: number) => Math.round((1 - alpha) * bg + alpha * c);
        const rOut = blend(Number(r), background[0]);
        const gOut = blend(Number(g), background[1]);
        const bOut = blend(Number(b), background[2]);
        return `rgb(${rOut}, ${gOut}, ${bOut})`;
    }
}

export default CommonService;
